import React, { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { useToast } from "../hooks/use-toast";
import { api } from "../lib/api";
import { Link2, Unlink } from "lucide-react";
import SsoButtons from "./SsoButtons";

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" aria-hidden="true">
    <path fill="#4285F4" d="M23.52 12.27c0-.82-.07-1.42-.22-2.04H12v4.1h6.52c-.13.98-.85 2.45-2.45 3.44l3.57 2.77c2.08-1.92 3.88-4.9 3.88-8.27z" />
    <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.94-2.92l-3.57-2.77c-.98.68-2.3 1.17-4.37 1.17-3.34 0-6.17-2.25-7.18-5.28L.76 17c1.95 3.98 6.04 7 11.24 7z" />
    <path fill="#FBBC05" d="M4.82 14.2c-.24-.68-.37-1.4-.37-2.2s.13-1.52.37-2.2L.76 6.6A11.96 11.96 0 0 0 0 12c0 1.92.46 3.74 1.24 5.4z" />
    <path fill="#EA4335" d="M12 4.75c2.28 0 3.82.98 4.7 1.8l3.18-3.1C17.95 1.52 15.24.24 12 .24 6.8.24 2.71 3.26.76 7.24l4.46 3.16c1.01-3.03 3.84-5.65 6.78-5.65z" />
  </svg>
);

const MicrosoftIcon = () => (
  <svg viewBox="0 0 23 23" className="w-6 h-6" aria-hidden="true">
    <path fill="#F25022" d="M1 1h10v10H1z" />
    <path fill="#7FBA00" d="M12 1h10v10H12z" />
    <path fill="#00A4EF" d="M1 12h10v10H1z" />
    <path fill="#FFB900" d="M12 12h10v10H12z" />
  </svg>
);

/**
 * Lets any user link/relink Google (and preview Microsoft, wireframe-only)
 * to their own account — independent of their role. Surfaced under
 * Settings > Account, which every logged-in user can see.
 */
export default function ConnectedAccounts() {
  const { toast } = useToast();
  const [status, setStatus] = useState(null);
  const [unlinking, setUnlinking] = useState(false);

  const load = () => api.get("/auth/sso-status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const unlinkGoogle = async () => {
    setUnlinking(true);
    try {
      await api.delete("/auth/google/link");
      toast({ title: "Google account unlinked" });
      load();
    } catch (e) {
      toast({ title: "Couldn't unlink", description: e?.response?.data?.detail, variant: "destructive" });
    } finally { setUnlinking(false); }
  };

  if (!status) return <div className="h-24 rounded-lg bg-muted/40 animate-pulse" />;

  return (
    <div className="space-y-4" data-testid="connected-accounts-section">
      <div className="space-y-2">
        <div className="flex items-center justify-between rounded-lg border border-border p-3">
          <div className="flex items-center gap-3">
            <GoogleIcon />
            <div>
              <div className="text-[13px] font-medium">Google</div>
              {status.google?.linked ? (
                <div className="text-[12px] text-muted-foreground" data-testid="google-linked-email">{status.google.email}</div>
              ) : (
                <div className="text-[12px] text-muted-foreground">Not connected</div>
              )}
            </div>
          </div>
          {status.google?.linked ? (
            <Button variant="outline" size="sm" className="gap-1.5" onClick={unlinkGoogle} disabled={unlinking} data-testid="google-unlink-btn">
              <Unlink className="w-3.5 h-3.5" /> Unlink
            </Button>
          ) : (
            <span className="text-[11px] text-muted-foreground">Use the button below to connect</span>
          )}
        </div>

        <div className="flex items-center justify-between rounded-lg border border-border p-3">
          <div className="flex items-center gap-3">
            <MicrosoftIcon />
            <div>
              <div className="text-[13px] font-medium">Microsoft</div>
              <div className="text-[12px] text-muted-foreground">Coming soon — wireframe preview only</div>
            </div>
          </div>
          <Link2 className="w-4 h-4 text-muted-foreground" />
        </div>
      </div>

      <SsoButtons intent="link" />
    </div>
  );
}
